import React, { useEffect } from 'react';
import { Table, Row, Col, FormGroup, Label, Button } from 'reactstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useRowSelect,
} from 'react-table';

import Select from 'react-select';
import { Icon } from '@assets';
import { useHistory } from 'react-router-dom';
import Avatar from 'react-avatar';

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input
          type='checkbox'
          className='custom-checkbox'
          ref={resolvedRef}
          {...rest}
        />
      </>
    );
  }
);

function GlobalFilter({
  preGlobalFilteredRows: _preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const [value, setValue] = React.useState(globalFilter);

  const onChange = useAsyncDebounce((val) => {
    setGlobalFilter(val || undefined);
  }, 200);

  return (
    <div className='search-bar d-inline-block inline-block'>
      <FormGroup className='mb-0 w-auto d-inline-block'>
        <div className='d-flex align-items-center w-100'>
          <Label
            for='agent-search'
            className='mb-0 w-auto d-inline-flex align-items-center mr-2'
          >
            <Icon icon='search' />
          </Label>
          <input
            id='agent-search'
            value={value || ''}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
            placeholder='Search agents, markets...'
            className='w-100'
          />
        </div>
      </FormGroup>
    </div>
  );
}

const statusColors = {
  true: '#16a34a',
  false: '#f97316',
};

const mobileStyles = {
  filterContainer: {
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '16px',
    background: '#f8fafc',
    boxShadow: '0 6px 18px rgba(15, 34, 58, 0.08)',
  },
  filterHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#475569',
  },
  filterLabel: {
    fontWeight: 600,
  },
  actionRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: '1 1 160px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 14px',
    borderRadius: '12px',
    boxShadow: '0 6px 16px rgba(15, 34, 58, 0.12)',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingBottom: '72px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 10px 28px rgba(15, 34, 58, 0.1)',
    border: '1px solid #eef2f7',
    cursor: 'pointer',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 auto',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1f2933',
  },
  statusBadge: (active) => ({
    fontSize: '13px',
    fontWeight: 600,
    color: statusColors[active],
    background: `${statusColors[active]}15`,
    borderRadius: '999px',
    padding: '6px 12px',
    textTransform: 'capitalize',
  }),
  detailSection: {
    marginTop: '12px',
    display: 'grid',
    rowGap: '8px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#94a3b8',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: '13px',
    color: '#334155',
    lineHeight: 1.45,
  },
  actionButtonsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '16px',
  },
  actionPrimary: {
    flex: '1 1 48%',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '10px 12px',
  },
  actionSecondary: {
    flex: '1 1 48%',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '10px 12px',
  },
};

export default function AgentListTable({
  columns,
  data,
  rightAddons = null,
  leftAddons = null,
  changeIsChecked,
  setSidebarState,
  onView,
  onToggleStatus,
  onDelete,
}) {
  const totalRecord = data.length;
  const pageSizeOptions = [
    { value: '10', label: '10' },
    { value: '15', label: '15' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '75', label: '75' },
    { value: '100', label: '100' },
    { value: totalRecord.toString(), label: 'All' },
  ];
  const defaultColumn = React.useMemo(
    () => ({
      width: 'auto',
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    rows,
    state,
    prepareRow,
  } = useTable(
    { columns, data, defaultColumn, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((cols) =>
        changeIsChecked
          ? [
              {
                id: 'selection',
                Header: ({ getToggleAllPageRowsSelectedProps }) => (
                  <IndeterminateCheckbox
                    {...getToggleAllPageRowsSelectedProps()}
                  />
                ),
                Cell: ({ row }) => (
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    onClick={(e) => e.stopPropagation()}
                  />
                ),
                width: 50,
                maxWidth: 50,
              },
              ...cols,
            ]
          : cols
      );
    }
  );

  const { pageIndex, sortBy, globalFilter } = state;

  const selectedRowSignature = React.useMemo(
    () =>
      selectedFlatRows
        .map((row) => row.id || row.original?.node?.id || '')
        .join('|'),
    [selectedFlatRows]
  );

  useEffect(() => {
    if (changeIsChecked) {
      changeIsChecked(selectedFlatRows);
    }
  }, [selectedRowSignature, changeIsChecked]);

  const history = useHistory();
  const desktopAddons = React.Children.toArray(rightAddons || []);
  const mobileActionButtons = desktopAddons
    .map((child, index) => {
      if (!React.isValidElement(child)) return null;
      const cleanedClasses = (child.props.className || '')
        .split(' ')
        .filter((cls) =>
          !['float-right', 'mr-2', 'mt-sm-0', 'mr-sm-3'].includes(cls)
        )
        .join(' ')
        .trim();
      return React.cloneElement(child, {
        key: index,
        className: `${cleanedClasses} mobile-action-btn`.trim(),
        style: { ...mobileStyles.actionButton, ...(child.props.style || {}) },
      });
    })
    .filter(Boolean);

  const sortLabel = React.useMemo(() => {
    if (!sortBy || !sortBy.length) return 'Default';
    const flatHeaders = headerGroups.flatMap((group) => group.headers);
    const match = flatHeaders.find((col) => col.id === sortBy[0].id);
    if (!match) return 'Custom';
    const headerValue = match.Header;
    if (typeof headerValue === 'string') return headerValue;
    if (typeof headerValue === 'function') return 'Custom';
    if (React.isValidElement(headerValue)) return 'Custom';
    return `${match.id}`.replace(/_/g, ' ');
  }, [sortBy, headerGroups]);

  return (
    <>
      <div className='d-none d-sm-block'>
        <Row className='my-2 d-flex align-items-center'>
          <Col sm='6'>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
            {leftAddons}
          </Col>
          <Col sm='6' className='text-lg-right'>
            {desktopAddons}
          </Col>
        </Row>

        <Table
          responsive
          className='client-list text-left min-w-900'
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps([
                      {
                        style: {
                          minWidth: column.minWidth,
                          width: column.width,
                          maxWidth: column.maxWidth,
                        },
                      },
                      column.getSortByToggleProps(),
                    ])}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <>
                            &nbsp; <Icon icon='sort-down' size='lg' />
                          </>
                        ) : (
                          <>
                            &nbsp; <Icon icon='sort-up' size='lg' />
                          </>
                        )
                      ) : column.canSort ? (
                        <>
                          &nbsp; <Icon icon='sort' size='lg' />
                        </>
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => {
                    const id = row.original?.node?.id;
                    if (onView) {
                      onView(Number(id));
                    } else {
                      setSidebarState(true);
                      history.push(`/agents/show/${id}`);
                    }
                  }}
                >
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
            {page.length <= 2 ? (
              <tr>
                <div className='spacer-div'></div>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>

      <div className='d-block d-sm-none'>
        <div style={mobileStyles.filterContainer}>
          <div style={mobileStyles.filterHeader}>
            <div onClick={(e) => e.stopPropagation()}>
              <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </div>
            {leftAddons ? (
              <div onClick={(e) => e.stopPropagation()}>{leftAddons}</div>
            ) : null}
            <div style={mobileStyles.filterRow}>
              <span style={mobileStyles.filterLabel}>{`${rows.length} Agent${rows.length === 1 ? '' : 's'}`}</span>
              <span>Sort By: {sortLabel}</span>
            </div>
          </div>
          <div style={mobileStyles.actionRow}>{mobileActionButtons}</div>
        </div>

        <div style={mobileStyles.listWrapper}>
          {page.map((row) => {
            prepareRow(row);
            const node = row.original?.node || {};
            const selectionProps = row.getToggleRowSelectedProps();
            const id = Number(node.id);

            const handleView = (e) => {
              e.stopPropagation();
              if (onView) {
                onView(id);
              } else {
                setSidebarState(true);
                history.push(`/agents/show/${id}`);
              }
            };

            const handleStatus = (e) => {
              e.stopPropagation();
              onToggleStatus && onToggleStatus(id, node.approved);
            };

            const handleDeleteClick = (e) => {
              e.stopPropagation();
              onDelete && onDelete(id);
            };

            return (
              <div
                key={row.id}
                style={mobileStyles.card}
                onClick={() => {
                  if (onView) {
                    onView(id);
                  } else {
                    setSidebarState(true);
                    history.push(`/agents/show/${id}`);
                  }
                }}
              >
                <div style={mobileStyles.headerRow}>
                  <div style={mobileStyles.headerLeft}>
                    <div style={mobileStyles.nameRow}>
                      <div onClick={(e) => e.stopPropagation()}>
                        <IndeterminateCheckbox {...selectionProps} />
                      </div>
                      <Avatar
                        name={node.name || 'Agent'}
                        size='32'
                        round
                        className='mr-2'
                      />
                      <span style={mobileStyles.title}>{node.name || 'Agent'}</span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: '13px' }}>
                      {node.brokerageName || 'No brokerage listed'}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                      {node.email || '-'}
                    </div>
                  </div>
                  <div>
                    <span style={mobileStyles.statusBadge(node.approved)}>
                      {node.approved ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div style={mobileStyles.detailSection}>
                  <div>
                    <div style={mobileStyles.detailLabel}>Phone</div>
                    <div style={mobileStyles.detailValue}>{node.phoneNo || '-'}</div>
                  </div>
                  <div>
                    <div style={mobileStyles.detailLabel}>License No</div>
                    <div style={mobileStyles.detailValue}>{node.licenseNo || '-'}</div>
                  </div>
                </div>

                <div
                  style={mobileStyles.actionButtonsRow}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    color='primary'
                    style={mobileStyles.actionPrimary}
                    onClick={handleView}
                  >
                    View Details
                  </Button>
                  {onToggleStatus ? (
                    <Button
                      color={node.approved ? 'warning' : 'success'}
                      style={mobileStyles.actionSecondary}
                      onClick={handleStatus}
                    >
                      {node.approved ? 'Inactivate' : 'Reactivate'}
                    </Button>
                  ) : null}
                  {onDelete ? (
                    <Button
                      color='danger'
                      style={mobileStyles.actionSecondary}
                      onClick={handleDeleteClick}
                    >
                      Delete
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Row className='d-flex align-items-center mt-4 flex-column flex-md-row'>
        <Col
          md='6'
          className='d-flex justify-content-md-start justify-content-center'
        >
          <FormGroup className='mb-3 mb-md-0'>
            <Label
              for='agent-page-size'
              className='mb-0 w-auto d-inline-block mr-2 mr-md-3'
            >
              Showing Entries
            </Label>
            <Select
              inputId='agent-page-size'
              name='PageSize'
              defaultValue={pageSizeOptions[0]}
              options={pageSizeOptions}
              onChange={(e) => {
                setPageSize(Number(e.value));
              }}
              className='basic-multi-select text-left'
              classNamePrefix='default-select'
              menuPlacement={'auto'}
            />
          </FormGroup>
        </Col>

        <Col
          md='6'
          className='d-flex justify-content-center mt-2 mt-md-0'
        >
          <ul className='pagination'>
            <li onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              <a>
                <Icon icon='chevron-left' />
                <Icon icon='chevron-left' />
              </a>
            </li>
            <li onClick={() => previousPage()} disabled={!canPreviousPage}>
              <a>
                <Icon icon='chevron-left' />
              </a>
            </li>
            <span className='d-flex align-items-center px-3'>
              Page &nbsp; <strong>{pageIndex + 1}</strong> &nbsp; of &nbsp;
              <strong>{pageOptions.length}</strong>
            </span>
            <li onClick={() => nextPage()} disabled={!canNextPage}>
              <a>
                <Icon icon='chevron-right' />
              </a>
            </li>
            <li onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              <a>
                <Icon icon='chevron-right' />
                <Icon icon='chevron-right' />
              </a>
            </li>
          </ul>
        </Col>
      </Row>
    </>
  );
}
